<template>
  <div v-if="isAdminUser && canLoadActivateNowPage" class="workato-activation">
    <div v-if="isLoading">
      <le-loader class="le-loader-center" :size="40"></le-loader>
    </div>
    <IhuModal id="legal-modal"
              :visible="isLegalModalVisible"
              @close="hideLegalModal">
      <div class="legal-modal-content">
        <div class="modal-title">
          LivePerson Workflows Terms of Service
        </div>
        <div class="mt-2-rem">
          By using the Workato workflow automation feature in conjunction with LivePerson’s platform,
          Company acknowledges and agrees:
          <ol>
            <li>
              To the  and
              <a class="legal-modal-help" href="https://www.workato.com/legal/terms-of-service" target="_blank">
                Workato Terms of Use
              </a>
              and
              <a class="legal-modal-help" href="https://www.workato.com/legal/privacy-policy" target="_blank">
                Workato Privacy Policy
              </a>
            </li>
            <li>The Workato service is hosted in AWS environment</li>
            <li>
              Use of Workato may incur additional fees as per Company’s binding agreement with
              LivePerson or LivePerson’s partner
            </li>
          </ol>
        </div>
        <div class="mt-2-rem">
          <IhuCheckbox :key="legalModalChecked"
                       :state="isLegalModalChecked"
                       :disabled="false"
                       class="mr-3 legal-modal-checkbox"
                       @change="toggleLegalModalChecked">
            I agree to Workato’s Terms of Service
          </IhuCheckbox>
        </div>
        <div class="legal-modal-footer mt-2-rem">
          <IhuButton variant="secondary"
                     @click="hideLegalModal">
            Cancel
          </IhuButton>
          <IhuButton variant="primary"
                     :disabled="!isLegalModalChecked"
                     class="ml-10px"
                     @click="() => createNewWorkatoAccountAndUserTrigger('enterWorkato')">
            Accept
          </IhuButton>
        </div>
      </div>
    </IhuModal>
    <div class="text-danger">{{ error }}</div>
    <div v-show="screenTransition('activateNow')" id="activate-now" class="workflow-landing">
      <div class="video-panel">
        <iframe v-tooltip="{ content: 'Workato Activate Title', show: false, trigger: 'manual' }"
                class="embedded-video"
                title="Workato Activate Title"
                :src="workatoActivationPageUrl">
        </iframe>
      </div>
      <div class="info-panel">
        <h1>Automate your workflow with Workato</h1>
        <div class="info-text">Activate our embedded Workato workspace inside LivePerson for more efficient integrations.</div>
        <IhuButton variant="primary" :disabled="isActivateButtonDisabled" @click="showLegalModal">Activate</IhuButton>
        <div class="info-panel-help mt-5-rem">
          <span class="info-text">New to Workato? </span>
          <a :href="workatoLearnMore" target="_blank">Learn more</a>
        </div>
      </div>
    </div>

    <div v-show="screenTransition('enterWorkato')" id="enter-workato" class="workflow-landing">
      <div class="video-panel">
        <iframe v-tooltip="{ content: 'Enter Workato Title', show: false, trigger: 'manual' }"
                class="embedded-video"
                title="Enter Workato"
                :src="workatoEnterPageUrl">
        </iframe>
      </div>
      <div class="info-panel">
        <div class="row info-panel-header">
          <i class="icon-49 greentick-3x margin-icon"></i>
          <h1 class="">
            You are the administrator now
          </h1>
        </div>
        <div v-if="roleName === 'LpBrandAdmin'" class="info-text">
          With your role you have full access to create and manage recipes.
        </div>
        <div v-if="roleName === 'LpAdmin'" class="info-text">
          With your role you have access to view and manage recipes.
        </div>
        <router-link :to="{ name: 'workato-integration' }" class="enter-workato-button">
          <IhuButton variant="primary">Enter Workato</IhuButton>
        </router-link>
        <div class="info-panel-help mt-5-rem">
          <span class="info-text">New to Workato? </span>
          <a :href="workatoLearnMore" target="_blank">Learn more</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { Vuex, Vue } from 'vue-infra';
  import leLoader from '@vueComponents/le-loader/le-loader.vue';
  import { MODULES_PATHS } from '@/store';
  import { workflowGuardMixin } from '@/mixins';
  import { WORKATO_CONFIG, HTTP_STATUS_CODE } from '@/common/consts';
  import { IhuButton, IhuCheckbox, IhuModal } from '@/components/common';
  import { ACTION_TYPES as WORKATO_CONFIGURATION_ACTION_TYPES } from '@/store/modules/data/workato-configuration/actions';
  import { GETTER_TYPES as BASE_COLLECTION_GETTER_TYPES } from '@/store/modules/data/workato-configuration/getters';
  import { appCode } from '../../../config';
  import { DATA_STATES } from '../../store/consts';

  const mapActions = Vuex.mapActions;
  const mapGetters = Vuex.mapGetters;

  export default {
    name: 'workato-activation',
    components: {
      IhuCheckbox,
      IhuModal,
      IhuButton,
      leLoader,
    },
    mixins: [workflowGuardMixin],
    props: [],
    data() {
      return {
        stage: 'activateNow',
        error: '',
        appCode,
        isLegalModalVisible: false,
        isLegalModalChecked: false,
        isAdminUser: false,
        isActivateButtonDisabled: false,
        canLoadActivateNowPage: false,
      };
    },
    computed: {
      ...mapGetters(MODULES_PATHS.DATA.WORKATO_SITE_SETTINGS, {
        getNewWorkatoAccount: BASE_COLLECTION_GETTER_TYPES.GET_NEW_WORKATO_ACCOUNT,
        getWorkatoToken: BASE_COLLECTION_GETTER_TYPES.GET_WORKATO_TOKEN,
        getExistingWorkatoAccount: BASE_COLLECTION_GETTER_TYPES.GET_EXISTING_WORKATO_ACCOUNT,
        getDataState: BASE_COLLECTION_GETTER_TYPES.GET_DATA_STATE,
        getWorkatoRecipe: BASE_COLLECTION_GETTER_TYPES.GET_WORKATO_RECIPE,
        getWorkatoConfigValues: BASE_COLLECTION_GETTER_TYPES.GET_WORKATO_CONFIG_VALUES,
      }),
      isLoading() {
        return this.getDataState === DATA_STATES.LOADING;
      },
      workatoActivationPageUrl() {
        return this.getConfigValue(WORKATO_CONFIG.WORKATO_ACTIVATE_PAGE_VIDEO_URL);
      },
      workatoEnterPageUrl() {
        return this.getConfigValue(WORKATO_CONFIG.WORKATO_ENTER_PAGE_VIDEO_URL);
      },
      workatoLearnMore() {
        return this.getConfigValue(WORKATO_CONFIG.WORKATO_LEARN_MORE);
      },
      workatoLegalTerms() {
        return this.getConfigValue(WORKATO_CONFIG.WORKATO_LEGAL_TERMS);
      },
    },
    async mounted() {
      await this.fetchIhubConfig({
        [WORKATO_CONFIG.WORKATO_ACTIVATE_PAGE_VIDEO_URL]: WORKATO_CONFIG.WORKATO_ACTIVATE_PAGE_VIDEO_URL,
        [WORKATO_CONFIG.WORKATO_ENTER_PAGE_VIDEO_URL]: WORKATO_CONFIG.WORKATO_ENTER_PAGE_VIDEO_URL,
        [WORKATO_CONFIG.WORKATO_LEARN_MORE]: WORKATO_CONFIG.WORKATO_LEARN_MORE,
        [WORKATO_CONFIG.WORKATO_LEGAL_TERMS]: WORKATO_CONFIG.WORKATO_LEGAL_TERMS,
      });
      await this.checkPermission();
    },
    methods: {
      ...mapActions(MODULES_PATHS.DATA.WORKATO_SITE_SETTINGS, {
        createNewWorkatoAccount: WORKATO_CONFIGURATION_ACTION_TYPES.CREATE_NEW_WORKATO_ACCOUNT,
        createNewWorkatoUser: WORKATO_CONFIGURATION_ACTION_TYPES.CREATE_NEW_WORKATO_USER,
        importWorkatoRecipe: WORKATO_CONFIGURATION_ACTION_TYPES.IMPORT_WORKATO_RECIPE,
        fetchExistingWorkatoAccount: WORKATO_CONFIGURATION_ACTION_TYPES.FETCH_EXISTING_WORKATO_ACCOUNT,
        fetchIhubConfig: WORKATO_CONFIGURATION_ACTION_TYPES.FETCH_IHUB_CONFIG,
        fetchWorkatoRecipe: WORKATO_CONFIGURATION_ACTION_TYPES.FETCH_WORKATO_RECIPE,
      }),
      getConfigValue(key) {
        return this.getWorkatoConfigValues[key] || null;
      },
      async createNewWorkatoAccountAndUserTrigger(newStage) {
        this.hideLegalModal();
        this.isActivateButtonDisabled = true;
        await this.fetchExistingWorkatoAccount();
        const existingWorkatoAccount = await this.getExistingWorkatoAccount;
        if (existingWorkatoAccount.responseCode === HTTP_STATUS_CODE.NOT_FOUND) {
          // Scenario for when there is no tenant, no imported recipes and no user
          try {
            await this.createNewWorkatoAccount();
            await this.importWorkatoRecipe();
            this.newUserResponse = await this.createNewWorkatoUser();
            this.roleName = this.newUserResponse.role_name;
            this.stage = newStage;
            await this.getIhubConfig(WORKATO_CONFIG.WORKATO_ENTER_PAGE_VIDEO_URL);
          } catch (error) {
            this.isActivateButtonDisabled = false;
            this.error = error.response.data;
          }
        } else if (existingWorkatoAccount.responseCode === HTTP_STATUS_CODE.OK) {
          try {
            await this.fetchWorkatoRecipe();
            const existingWorkatoRecipe = await this.getWorkatoRecipe;
            if (existingWorkatoRecipe.responseCode === HTTP_STATUS_CODE.OK && existingWorkatoRecipe.premadeRecipe.length === 0) {
              // Scenario for when there is a tenant, no imported recipes and no user
              try {
                await this.importWorkatoRecipe();
                this.newUserResponse = await this.createNewWorkatoUser();
                this.roleName = this.newUserResponse.role_name;
                this.stage = newStage;
                await this.getIhubConfig(WORKATO_CONFIG.WORKATO_ENTER_PAGE_VIDEO_URL);
              } catch (error) {
                this.isActivateButtonDisabled = false;
                this.error = error.response.data;
              }
            } else if (existingWorkatoRecipe.responseCode === HTTP_STATUS_CODE.OK && existingWorkatoRecipe.premadeRecipe.length > 0) {
              try {
                // Scenario for when there is a tenant, imported recipes and no user
                this.newUserResponse = await this.createNewWorkatoUser();
                this.roleName = this.newUserResponse.role_name;
                this.stage = newStage;
                await this.getIhubConfig(WORKATO_CONFIG.WORKATO_ENTER_PAGE_VIDEO_URL);
              } catch (error) {
                this.isActivateButtonDisabled = false;
                this.error = error.response.data;
              }
            }
          } catch (error) {
            this.isActivateButtonDisabled = false;
            this.error = error.response.data;
          }
        }
      },
      toggleLegalModalChecked() {
        this.isLegalModalChecked = !this.isLegalModalChecked;
      },
      showLegalModal() {
        this.isLegalModalChecked = false;
        this.isLegalModalVisible = true;
      },
      hideLegalModal() {
        this.isLegalModalVisible = false;
      },
      screenTransition(screen) {
        return this.stage === screen;
      },
      async checkPermission() {
        const store = new Vue().$store;
        this.isAdminUser = store.getters['session/getConfig']().isLPA || store.getters['session/getConfig']().isAdmin;
        this.workatoTokenResponse = await this.getWorkatoToken;
        this.token = this.workatoTokenResponse.token;
        if (this.isAdminUser && !this.token && !(this.isTenantCreated && this.isAccountCreated)) {
          this.canLoadActivateNowPage = true;
        }
      },
      async getIhubConfig(workatoVideoConfig) {
        const responseObject = await this.fetchIhubConfig(workatoVideoConfig);
        return responseObject.configValue;
      },
    },
  };


</script>

<style scoped lang="scss">
@import './workato';

.icon-49 {
  background-repeat: no-repeat;
  height: 49px;
  width: 49px;
  background-size: 49px;
}
.workato-activation {
  .legal-modal-content {
    text-align: left;
    padding: 4rem;
    font-size: 14px !important;
    line-height: 150%;
    width: 683px;
    @include themify {
      color: getColor('legal-modal-content-color', 'ihu');
      background-color: getColor('premade-recipe-background', 'ihu') !important;
    }

    .modal-title{
      font-size: 16.8px !important;
      @include themify {
        color: getColor('legal-modal-header-color', 'ihu') !important;
      }
    }

    ol {
      list-style: auto;
      margin-left: 20px;
    }

    .legal-modal-checkbox {
      @include themify {
        color: getColor('legal-modal-content-color', 'ihu') !important;
      }
      /deep/ .label {
        margin-left: 5px;
        font-size: 14px;
      }
    }

    .legal-modal-help {
      text-decoration: none;
      @include themify {
        color: getColor('legal-modal-hyperlink-color', 'ihu');
      }
    }
    .legal-modal-footer {
      float: right;
      display: flex;
      padding-bottom: 4rem;
      .ml-10px {
        margin-left: 24px;
      }
    }
  }
}
</style>
